<template>
  <AdminLayout
    page="Alert"
    title="Alert"
    header="Send Alert to "
    :include-page-in-header="true"
  >
    <div v-if="isLoading"><Loading /></div>
    <div v-else>
      <div v-if="hasPermission(PermissionType.SendAlerts)">
        <!-- Navigation link back to multi-building Send page-->
        <div class="mb-4 ml-4 -mt-4">
          <router-link
            to="multialert"
            class="inline-flex items-center text-blue-600 hover:text-blue-800 underline"
          >
            Send to Multiple Buildings at once
          </router-link>
        </div>

        <!-- Message Input -->
        <div class="mb-6 pt-6">
          <div class="flex flex-auto flex-wrap flex-row justify-between items-center">
            <h2 class="text-2xl font-medium text-gray-900 w-full sm:w-1/2">
              Alert Message
            </h2>
            <Listbox v-model="selectedTemplate" class="w-full sm:w-1/2 mb-3">
              <div class="relative mt-3">
                <ListboxButton
                  class="relative w-full py-2 pl-3 pr-10 text-left bg-white rounded-lg border shadow-md cursor-default focus:outline-none focus-visible:ring-2 focus-visible:ring-opacity-75 focus-visible:ring-white focus-visible:ring-offset-orange-300 focus-visible:ring-offset-2 focus-visible:border-indigo-500 sm:text-sm"
                >
                  <span class="text-gray-800' text-md">
                    {{
                      alertTemplates.length > 0
                        ? selectedTemplate
                          ? selectedTemplate.name
                          : "-- select a template --"
                        : "-- no alert templates --"
                    }}
                  </span>
                  <span
                    class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none"
                  >
                    <i-heroicons-solid-selector
                      class="text-gray-600 w-5 h-5 text-2xl"
                      aria-hidden="true"
                    />
                  </span>
                </ListboxButton>
                <transition
                  leave-active-class="transition duration-100 ease-in"
                  leave-from-class="opacity-100"
                  leave-to-class="opacity-0"
                >
                  <ListboxOptions
                    class="absolute w-full py-1 mt-1 z-20 overflow-auto text-base bg-white rounded-md shadow-lg max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
                  >
                    <ListboxOption
                      v-for="template in alertTemplates"
                      :key="template.templateId"
                      :value="template"
                    >
                      <li
                        :class="[
                          selectedTemplate
                            ? selectedTemplate.templateId
                            : -1 == template.templateId
                            ? 'border-1 bg-sky-300 border-blue-500'
                            : 'hover:bg-blue-200',
                          'text-gray-900 select-none relative py-1 pl-4 pr-4 cursor-pointer',
                        ]"
                      >
                        {{ template.name }}
                      </li>
                    </ListboxOption>
                    <ListboxOption :value="null">
                      <router-link
                        v-if="hasPermission(PermissionType.SendAlerts)"
                        class="block underline text-blue-800 mb-4 ml-4 mt-3"
                        to="alert/templates"
                      >
                        Edit templates
                      </router-link>
                    </ListboxOption>
                  </ListboxOptions>
                </transition>
              </div>
            </Listbox>
          </div>
          <BaseTextarea
            v-model="message"
            placeholder="Enter a message"
            class="w-full h-64 bg-gray-200 rounded-lg resize-none"
          />
        </div>

        <!-- Recipients - Non-Staff occupant filters -->
        <div class="mb-6 pt-6">
          <h3 class="text-xl font-medium text-gray-900 mb-3">Recipients</h3>

          <SwitchGroup>
            <div class="flex items-center">
              <Switch
                v-model="sendAllTenants"
                :class="sendAllTenants ? 'bg-blue-600' : 'bg-gray-200'"
                class="relative inline-flex h-6 w-11 items-center rounded-full"
              >
                <span
                  :class="sendAllTenants ? 'translate-x-6' : 'translate-x-1'"
                  class="inline-block h-4 w-4 transform rounded-full bg-white transition"
                />
              </Switch>
              <SwitchLabel class="ml-2 select-none text-lg">
                Send to all Tenants
              </SwitchLabel>
            </div>
          </SwitchGroup>
        </div>

        <div v-if="!sendAllTenants" class="flex gap-4 mb-3">
          <div class="basis-1/3 z-10">
            <Listbox v-model="selectedFloors" multiple>
              <div class="relative mt-3">
                <ListboxButton
                  class="relative w-full py-2 pl-3 pr-10 text-left bg-white rounded-lg border shadow-md cursor-default focus:outline-none focus-visible:ring-2 focus-visible:ring-opacity-75 focus-visible:ring-white focus-visible:ring-offset-orange-300 focus-visible:ring-offset-2 focus-visible:border-indigo-500 sm:text-sm"
                >
                  <span class="text-gray-800 text-md">
                    {{
                      selectedFloors.length > 0
                        ? selectedFloors.join(", ")
                        : "-- select floors --"
                    }}
                  </span>
                  <span
                    class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none"
                  >
                    <i-heroicons-solid-selector
                      class="text-gray-600 w-5 h-5 text-2xl"
                      aria-hidden="true"
                    />
                  </span>
                </ListboxButton>
                <transition
                  leave-active-class="transition duration-100 ease-in"
                  leave-from-class="opacity-100"
                  leave-to-class="opacity-0"
                >
                  <ListboxOptions
                    class="absolute w-full py-1 mt-1 overflow-auto text-base bg-white rounded-md shadow-lg max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
                  >
                    <ListboxOption
                      v-for="floor in property.floorList"
                      :key="floor"
                      :value="floor"
                      as="template"
                    >
                      <li
                        :class="[
                          selectedFloors.includes(floor)
                            ? 'border-1 bg-sky-300 border-blue-500'
                            : 'hover:bg-blue-200',
                          'text-gray-900 select-none relative py-1 pl-4 pr-4 cursor-pointer',
                        ]"
                      >
                        {{ floor }}
                      </li>
                    </ListboxOption>
                  </ListboxOptions>
                </transition>
              </div>
            </Listbox>
            <button
              class="text-sm font-medium p-1 mt-1 text-blue-900 bg-transparent border border-transparent rounded-md hover:bg-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500"
              @click="() => (selectedFloors = property.floorList)"
            >
              SEND TO ALL FLOORS
            </button>
            <button
              class="text-sm font-medium p-1 mt-1 text-blue-900 bg-transparent border border-transparent rounded-md hover:bg-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500"
              @click="() => (selectedFloors = [])"
            >
              CLEAR FLOORS
            </button>
          </div>
          <div class="basis-1/3">
            <Listbox v-model="selectedTenants" multiple>
              <div class="relative mt-3">
                <ListboxButton
                  class="relative w-full py-2 pl-3 pr-10 text-left bg-white rounded-lg border shadow-md cursor-default focus:outline-none focus-visible:ring-2 focus-visible:ring-opacity-75 focus-visible:ring-white focus-visible:ring-offset-orange-300 focus-visible:ring-offset-2 focus-visible:border-indigo-500 sm:text-sm"
                >
                  <span class="text-gray-800 text-md">
                    {{
                      selectedTenants.length > 0
                        ? selectedTenants.map((t) => t.name).join(", ")
                        : "-- select tenants --"
                    }}
                  </span>
                  <span
                    class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none"
                  >
                    <i-heroicons-solid-selector
                      class="text-gray-600 w-5 h-5 text-2xl"
                      aria-hidden="true"
                    />
                  </span>
                </ListboxButton>
                <transition
                  leave-active-class="transition duration-100 ease-in"
                  leave-from-class="opacity-100"
                  leave-to-class="opacity-0"
                >
                  <ListboxOptions
                    class="absolute w-full py-1 mt-1 overflow-auto text-base bg-white rounded-md shadow-lg max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm z-10"
                  >
                    <ListboxOption
                      v-for="tenant in property.tenants"
                      :key="tenant.tenantId"
                      :value="tenant"
                      as="template"
                    >
                      <li
                        :class="[
                          selectedTenants.map((t) => t.tenantId).includes(tenant.tenantId)
                            ? 'border-1 bg-sky-300 border-blue-500'
                            : 'hover:bg-blue-200',
                          'text-gray-900 select-none relative py-1 pl-4 pr-4 cursor-pointer',
                        ]"
                      >
                        {{ tenant.name }}
                      </li>
                    </ListboxOption>
                  </ListboxOptions>
                </transition>
              </div>
            </Listbox>
            <button
              class="text-sm font-medium p-1 mt-1 text-blue-900 bg-transparent border border-transparent rounded-md hover:bg-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500"
              @click="() => (selectedTenants = property.tenants)"
            >
              SEND TO ALL TENANTS
            </button>
            <button
              class="text-sm font-medium p-1 mt-1 text-blue-900 bg-transparent border border-transparent rounded-md hover:bg-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500"
              @click="() => (selectedTenants = [])"
            >
              CLEAR TENANTS
            </button>
          </div>
          <div class="basis-1/3">
            <Listbox v-model="selectedNonStaffRolesAndCollections" multiple>
              <div class="relative mt-3">
                <ListboxButton
                  class="relative w-full py-2 pl-3 pr-10 text-left bg-white rounded-lg border shadow-md cursor-default focus:outline-none focus-visible:ring-2 focus-visible:ring-opacity-75 focus-visible:ring-white focus-visible:ring-offset-orange-300 focus-visible:ring-offset-2 focus-visible:border-indigo-500 sm:text-sm"
                >
                  <span class="text-gray-800 text-md">
                    {{
                      selectedNonStaffRolesAndCollections.length > 0
                        ? selectedRoles
                            .map((r) => r.roleAlias)
                            .concat(
                              selectedNonStaffCollections.map((c) => c.userCollectionName)
                            )
                            .join(", ")
                        : "-- select roles --"
                    }}
                  </span>
                  <span
                    class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none"
                  >
                    <i-heroicons-solid-selector
                      class="text-gray-600 w-5 h-5 text-2xl"
                      aria-hidden="true"
                    />
                  </span>
                </ListboxButton>
                <transition
                  leave-active-class="transition duration-100 ease-in"
                  leave-from-class="opacity-100"
                  leave-to-class="opacity-0"
                >
                  <ListboxOptions
                    class="absolute w-full py-1 mt-1 overflow-auto text-base bg-white rounded-md shadow-lg max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
                  >
                    <ListboxOption
                      v-for="role in nonStaffRoles"
                      :key="role.siteRoleId"
                      :value="role"
                      as="template"
                    >
                      <li
                        :class="[
                          selectedRoles.map((r) => r.siteRoleId).includes(role.siteRoleId)
                            ? 'border-1 bg-sky-300 border-blue-500'
                            : 'hover:bg-blue-200',
                          'text-gray-900 select-none relative py-1 pl-4 pr-4 cursor-pointer',
                        ]"
                      >
                        {{ role.roleAlias }}
                      </li>
                    </ListboxOption>
                    <ListboxOption
                      v-for="userCollection in nonStaffCollections"
                      :key="userCollection.userCollectionId"
                      :value="userCollection"
                      as="template"
                    >
                      <li
                        :class="[
                          selectedNonStaffCollections
                            .map((c) => c.userCollectionId)
                            .includes(userCollection.userCollectionId)
                            ? 'border-1 bg-sky-300 border-blue-500'
                            : 'hover:bg-blue-200',
                          'text-gray-900 select-none relative py-1 pl-4 pr-4 cursor-pointer',
                        ]"
                      >
                        {{ userCollection.userCollectionName }}
                      </li>
                    </ListboxOption>
                  </ListboxOptions>
                </transition>
              </div>
            </Listbox>
            <button
              class="text-sm font-medium p-1 mt-1 text-blue-900 bg-transparent border border-transparent rounded-md hover:bg-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500"
              @click="() => (selectedRoles = nonStaffRoles)"
            >
              SEND TO ALL ROLES
            </button>
            <button
              class="text-sm font-medium p-1 mt-1 text-blue-900 bg-transparent border border-transparent rounded-md hover:bg-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500"
              @click="() => (selectedRoles = [])"
            >
              CLEAR ROLES
            </button>
          </div>
        </div>

        <!--#region Recipients - Staff filters -->
        <SwitchGroup class="mt-1 z-0">
          <div class="flex items-center">
            <Switch
              v-model="sendAllStaff"
              :class="sendAllStaff ? 'bg-blue-600' : 'bg-gray-200'"
              class="relative inline-flex h-6 w-11 items-center rounded-full z-0"
            >
              <span
                :class="sendAllStaff ? 'translate-x-6' : 'translate-x-1'"
                class="inline-block h-4 w-4 transform rounded-full bg-white transition"
              />
            </Switch>
            <SwitchLabel class="ml-2 select-none text-lg">Send to all staff</SwitchLabel>
          </div>
        </SwitchGroup>
        <div v-if="!sendAllStaff" class="mt-8 ml-10">
          <BaseCheckboxGroup
            v-model="selectedStaffRoles"
            :options="staffRoles"
            class="inline"
          />
          <div v-if="staffCollections.length > 0" class="mt-4 mb-0">
            Staff Collections
          </div>
          <BaseCheckboxGroup
            v-if="staffCollections.length > 0"
            v-model="selectedStaffUserCollections"
            :options="staffCollections"
          />
          <button
            class="text-sm font-medium p-1 mt-1 text-blue-900 bg-transparent border border-transparent rounded-md hover:bg-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500"
            @click="
              () => {
                selectedStaffRoles = [];
                selectedStaffUserCollections = [];
              }
            "
          >
            UNCHECK ALL STAFF
          </button>
        </div>

        <!-- Additional Recipients -->
        <div class="mb-6 pt-6">
          <h3 class="text-xl font-medium text-gray-900 mb-3">Additional Recipients</h3>
          <div v-if="additionalRecipients.length > 0" class="flex flex-wrap gap-2 mb-3">
            <div
              v-for="(phoneNumber, index) in additionalRecipients"
              :key="phoneNumber"
              class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800"
            >
              {{ formatPhoneNumber(phoneNumber) }}
              <button
                @click="additionalRecipients.splice(index, 1)"
                class="ml-2 inline-flex items-center justify-center w-4 h-4 text-blue-400 hover:text-blue-600"
              >
                <i-heroicons-solid-x class="w-3 h-3" />
              </button>
            </div>
          </div>
          <div class="flex gap-2">
            <input
              v-model="additionalRecipientText"
              type="tel"
              placeholder="Phone number"
              class="border rounded px-3 py-2 flex-1 max-w-xs"
              @keyup.enter="addRecipient"
            />
            <button
              @click="addRecipient"
              class="px-4 py-2 text-sm font-medium text-blue-900 bg-blue-200 rounded hover:bg-blue-300"
            >
              Add Number
            </button>
          </div>
        </div>

        <!-- View All Recipients Button and Popup -->
        <div v-if="isCountLoading"><Loading /></div>
        <div v-else>
          <button
            class="text-sm font-medium py-2 px-4 mt-2 text-blue-900 bg-blue-200 border border-transparent rounded-md hover:bg-blue-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500"
            @click="ViewAllRecipients"
          >
            View All Recipients ({{ filteredCount + additionalRecipients.length }})
          </button>
          <BasePopup
            :is-visible="isRecipientViewerVisible"
            title="All Recipients"
            :is-full-width="true"
            @close="isRecipientViewerVisible = false"
          >
            <div v-if="isAllRecipientsLoading"><Loading /></div>
            <div v-else>
              <table class="table-auto w-full mt-4 border-2 shadow text-sm">
                <thead class="font-sm font-bold">
                  <tr>
                    <td
                      class="cursor-pointer hover:bg-sky-300 hover:text-black"
                      @click="selectedSortType = SortType.Name"
                    >
                      <span class="flex has-tooltip">
                        <span
                          class="tooltip text-xs shadow-lg p-1 pl-3 bg-gray-200 text-gray-700 -mt-8"
                          >Click to sort by Recipient Name</span
                        >
                        Name
                        <IHeroiconsOutlineChevronDown
                          class="ml-2 mt-1 text-gray-500"
                          v-if="selectedSortType !== SortType.Name"
                        />
                      </span>
                    </td>
                    <td class="cursor-pointer" @click="selectedSortType = SortType.Role">
                      <span class="flex has-tooltip">
                        <span
                          class="tooltip text-xs shadow-lg p-1 pl-3 bg-gray-200 text-gray-700 -mt-8"
                          >Click to sort by Recipient Role</span
                        >
                        Role
                        <IHeroiconsOutlineChevronDown
                          v-if="selectedSortType !== SortType.Role"
                        />
                      </span>
                    </td>
                    <td
                      class="cursor-pointer"
                      @click="selectedSortType = SortType.Tenant"
                    >
                      <span class="flex has-tooltip">
                        <span
                          class="tooltip text-xs shadow-lg p-1 pl-3 bg-gray-200 text-gray-700 -mt-8"
                          >Click to sort by Recipient's Tenant</span
                        >
                        Tenant
                        <IHeroiconsOutlineChevronDown
                          v-if="selectedSortType !== SortType.Tenant"
                        />
                      </span>
                    </td>
                    <td
                      class="cursor-pointer"
                      @click="selectedSortType = SortType.Floors"
                    >
                      <span class="flex has-tooltip">
                        <span
                          class="tooltip text-xs shadow-lg p-1 pl-3 bg-gray-200 text-gray-700 -mt-8"
                          >Click to sort by Recipient's Floor(s)</span
                        >
                        Floors
                        <IHeroiconsOutlineChevronDown
                          v-if="selectedSortType !== SortType.Floors"
                        />
                      </span>
                    </td>
                    <td class="cursor-pointer" @click="selectedSortType = SortType.Push">
                      <span class="flex has-tooltip">
                        <span
                          class="tooltip text-xs shadow-lg p-1 pl-3 bg-gray-200 text-gray-700 -mt-8"
                          >Click to sort by Push Device Count</span
                        >
                        Push
                        <IHeroiconsOutlineChevronDown
                          v-if="selectedSortType !== SortType.Push"
                        />
                      </span>
                    </td>
                    <td class="cursor-pointer" @click="selectedSortType = SortType.Email">
                      <span class="flex has-tooltip">
                        <span
                          class="tooltip text-xs shadow-lg p-1 pl-3 bg-gray-200 text-gray-700 -mt-8"
                          >Click to sort by Recipient's Email</span
                        >

                        Alert Email
                        <IHeroiconsOutlineChevronDown
                          v-if="selectedSortType !== SortType.Email"
                        />
                      </span>
                    </td>
                    <td class="cursor-pointer" @click="selectedSortType = SortType.Phone">
                      <span class="flex has-tooltip">
                        <span
                          class="tooltip text-xs shadow-lg p-1 pl-3 bg-gray-200 text-gray-700 -mt-8"
                          >Click to sort by Recipient's Phone</span
                        >

                        Alert Phone
                        <IHeroiconsOutlineChevronDown
                          v-if="selectedSortType !== SortType.Phone"
                        />
                      </span>
                    </td>
                  </tr>
                </thead>
                <tr
                  v-for="(recipient, i) in sortedRecipients"
                  :key="i"
                  class="font-xs font-mono hover:border-sky-800 border-bottom-2 hover:bg-sky-300"
                  :class="i % 2 ? 'bg-stone-200' : ''"
                >
                  <td>{{ recipient.firstName }} {{ recipient.lastName }}</td>
                  <td>{{ recipient.roleAlias }}</td>
                  <td>{{ recipient.tenantName }}</td>
                  <td :title="recipient.alertSubscribedFloors || 'All floors'">
                    {{
                      recipient.alertSubscribedFloors?.length > 10
                        ? recipient.alertSubscribedFloors.substring(0, 10) + "..."
                        : recipient.alertSubscribedFloors || "All floors"
                    }}
                  </td>
                  <td>{{ recipient.pushSubscriptionCount || "" }}</td>
                  <td>{{ recipient.contactEmail }}</td>
                  <td :class="recipient.isSmsOptedOut ? 'text-red-600' : ''">
                    {{ formatPhoneNumber(recipient.contactSms)
                    }}{{
                      recipient.isSmsOptedOut ? " (Opted Out, will not receive text)" : ""
                    }}
                  </td>
                </tr>
              </table>
              <div v-if="additionalRecipients.length > 0">
                <h1 class="mt-5">Additional Recipients:</h1>
                <p
                  v-for="additionalRecipient in additionalRecipients"
                  :key="additionalRecipient"
                >
                  {{ formatPhoneNumber(additionalRecipient) }}
                </p>
              </div>
            </div>
          </BasePopup>

          <BasePopup
            :is-visible="isConfirmDialogVisible"
            title="Confirm Send Alert Message:"
            :is-full-width="true"
            @close="isConfirmDialogVisible = false"
          >
            <div>
              <div v-if="isSending">
                <div>
                  <Loading />
                </div>
                <div>
                  <h1
                    class="mt-5 mb-5 bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded relative"
                    role="alert"
                  >
                    Sending is now in progress. This process can occasionally take several
                    minutes.<br />
                    Please do not reload page or press send again until complete.
                  </h1>
                </div>
              </div>
              <!-- <div
                v-if="errorSending"
                class="mt-5 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative"
                role="alert"
              >
                <span class="block sm:inline">{{ errorText }}</span>
              </div> -->
              <div>
                Do you want to send the this Alert to
                <span>{{ recipientCount }}</span>
                Alert recipients?
              </div>
              <div class="mx-6 my-4 italic">&quot;{{ message }}&quot;</div>

              <div class="float-right">
                <button
                  class="text-lg font-medium px-4 py-2 mx-4 mt-5 text-blue-900 bg-blue-200 border border-transparent rounded-md hover:bg-blue-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500"
                  @click="sendAlert"
                >
                  Send Now
                </button>
                <button
                  class="text-lg font-medium px-4 py-2 mt-5 text-blue-900 bg-gray-200 border border-transparent rounded-md hover:bg-blue-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500"
                  @click="isConfirmDialogVisible = false"
                >
                  Cancel
                </button>
              </div>
            </div>
          </BasePopup>
        </div>

          <!-- Send Alert Button -->
        <div class="flex gap-4 mt-6 pt-0">
          <button
            @click="confirmSendAlert"
            :disabled="isSending"
            class="px-6 py-3 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            <span v-if="isSending">Sending...</span>
            <span v-else
              >Send Alert to {{ recipientCount.toLocaleString() }} Recipients</span
            >
          </button>
        </div>

        <!-- Success/Error Messages -->
        <div v-if="errorSending || successSending" class="mt-4">
          <div
            v-if="errorSending"
            class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded  "
            role="alert"
          >
            <div class=" text-lg ">{{ errorText }}</div>
            <div class=" mt-3 ml-3 font-mono text-sm ">{{ additionalErrorText }}</div>
          </div>
          <div
            v-if="successSending"
            class="p-4 text-green-700 bg-green-100 rounded-lg"
            role="alert"
          >
            <span class="block sm:inline">Successfully sent alert!</span>
          </div>
        </div>


      </div>
      <div v-else>You need permission to view this page.</div>
      <div class="flex flex-col mt-8">
        <router-link
          v-if="hasPermission(PermissionType.ViewAlertSubscriptions)"
          class="underline text-blue-800 mb-2"
          to="alert/subscriptions"
        >
          Alert Subscriptions Report
        </router-link>
        <router-link
          v-if="hasPermission(PermissionType.SendAlerts)"
          class="underline text-blue-800 mb-2"
          to="alert/history"
        >
          Alert History
        </router-link>

        <router-link
          v-if="hasPermission(PermissionType.SendAlerts) && property.isMultiAlertEnabled"
          class="underline text-blue-800 mb-2"
          to="multialert"
        >
          Send to Multiple Buildings at Once
        </router-link>
      </div>
    </div>
  </AdminLayout>
</template>

<script setup lang="ts">
import { useBldgStore } from "../../store/bldgStore";
import { useUserStore } from "../../store/userStore";
import {
  TemplateDTO,
  PermissionType,
  TenantDTO,
  SiteRoleDTO,
  FilteredAlertRecipientsRequest,
  FilteredAlertRecipientsCountRequest,
  AlertFilterDTO,
  SendAlertRequest,
  UserCollectionDTO,
} from "../../store/generatedFetchClient";
import { useAlertStore } from "../../store/alertStore";
import {
  Listbox,
  ListboxButton,
  ListboxOptions,
  ListboxOption,
  SwitchGroup,
  Switch,
  SwitchLabel,
} from "@headlessui/vue";
import { formatPhoneNumber } from "../../helpers/index";

const bldgStore = useBldgStore();
const userStore = useUserStore();
const alertStore = useAlertStore();

var property = computed(() => bldgStore.building);
const nonStaffRoles = computed(() =>
  property.value.siteRoles.filter((r) => !r.isStaffRole)
);
const staffRoles = computed(() =>
  property.value.siteRoles
    .filter((r) => r.isStaffRole)
    .map((r) => {
      return { name: r.roleAlias, value: r.siteRoleId };
    })
);

const nonStaffCollections = computed(() =>
  property.value.userCollections.filter((c) => !c.isStaff)
);

const staffCollections = computed(() =>
  property.value.userCollections
    .filter((c) => c.isStaff)
    .map((c) => {
      return { name: c.userCollectionName, value: c.userCollectionId };
    })
);

const hasPermission = userStore.hasPermission;

var isLoading = ref(false);
onMounted(loadData);
watch(property, loadData);

async function loadData() {
  isLoading.value = true;
  if (property?.value?.portalId) {
    await alertStore.getTemplates(property.value.portalId);

    isCountLoading.value = true;
    getRecipientCount();
    isCountLoading.value = false;
  }
  isLoading.value = false;
}

var alertTemplates = computed<TemplateDTO[]>(() => alertStore.templates);
const selectedTemplate = ref(null as TemplateDTO);

const message = ref("");

watch(selectedTemplate, () => {
  console.log(selectedTemplate.value);
  message.value = selectedTemplate.value.template;
});

const sendAllTenants = ref(true);

const selectedFloors = ref([] as string[]);
watch(selectedFloors, () => {
  selectedFloors.value.sort(
    (a, b) => property.value.floorList.indexOf(a) - property.value.floorList.indexOf(b)
  );
});

const selectedTenants = ref([] as TenantDTO[]);
watch(selectedTenants, () => {
  selectedTenants.value.sort(
    (a, b) => property.value.tenants.indexOf(a) - property.value.tenants.indexOf(b)
  );
});

const selectedNonStaffRolesAndCollections = ref(
  [] as (SiteRoleDTO | UserCollectionDTO)[]
);

function isRole(
  roleOrUserCollection: SiteRoleDTO | UserCollectionDTO
): roleOrUserCollection is SiteRoleDTO {
  return (roleOrUserCollection as SiteRoleDTO).siteRoleId !== undefined;
}

function isCollection(
  roleOrUserCollection: SiteRoleDTO | UserCollectionDTO
): roleOrUserCollection is UserCollectionDTO {
  return !isRole(roleOrUserCollection);
}

const selectedRoles = computed(() =>
  selectedNonStaffRolesAndCollections.value
    .filter((s) => isRole(s))
    .map((c) => c as SiteRoleDTO)
    .sort(
      (a, b) => property.value.siteRoles.indexOf(a) - property.value.siteRoles.indexOf(b)
    )
);

const selectedNonStaffCollections = computed(() =>
  selectedNonStaffRolesAndCollections.value
    .filter((s) => isCollection(s))
    .map((c) => c as UserCollectionDTO)
);

const sendAllStaff = ref(true);

const selectedStaffRoles = ref([] as number[]);
const selectedStaffUserCollections = ref([] as number[]);

const additionalRecipientText = ref("");
const additionalRecipients = ref([] as string[]);

const filteredRecipients = ref([] as AlertFilterDTO[]);
const isCountLoading = ref(false);
const filteredCount = ref(0);

var recipientCount = computed(() => {
  let count = filteredCount.value + additionalRecipients.value.length;
  //if (additionalRecipientText.value?.trim()?.length > 0) count++;
  return count;
});

function addRecipient() {
  if (
    additionalRecipientText.value.length > 0 &&
    !additionalRecipients.value.includes(additionalRecipientText.value)
  ) {
    additionalRecipients.value.push(additionalRecipientText.value);
    additionalRecipientText.value = "";
  }
}

watch(sendAllTenants, getRecipientCount);
watch(sendAllStaff, getRecipientCount);
watch(selectedFloors, getRecipientCount);
watch(selectedTenants, getRecipientCount);
watch(selectedRoles, getRecipientCount);
watch(selectedNonStaffCollections, getRecipientCount);
watch(selectedStaffRoles.value, getRecipientCount);
watch(selectedStaffUserCollections.value, getRecipientCount);

async function getRecipientCount() {
  isCountLoading.value = true;

  var filter: FilteredAlertRecipientsCountRequest = {
    portalIds: [property.value.portalId],
    floors: selectedFloors.value,
    staffRoles: selectedStaffRoles.value,
    nonStaffRoles: selectedRoles.value.map((r) => r.siteRoleId),
    tenants: selectedTenants.value.map((t) => t.tenantId),
    staffCollections: selectedStaffUserCollections.value,
    nonStaffCollections: selectedNonStaffCollections.value.map((c) => c.userCollectionId),
    allTenants: sendAllTenants.value,
    allStaff: sendAllStaff.value,
  };

  // If all floors selected, set to null (null is all floors)
  if (property.value.floorList.length == filter.floors.length) {
    filter.floors = null;
  }

  filteredCount.value = await alertStore.getFilteredCount(filter);
  isCountLoading.value = false;
}

let isRecipientViewerVisible = ref(false);
let isConfirmDialogVisible = ref(false);
let isAllRecipientsLoading = ref(false);

async function ViewAllRecipients() {
  isAllRecipientsLoading.value = true;
  isRecipientViewerVisible.value = true;
  await getRecipients();
  isAllRecipientsLoading.value = false;
  console.log("all recipients", filteredRecipients.value);
}

async function getRecipients() {
  var filter: FilteredAlertRecipientsRequest = {
    portalIds: [property.value.portalId],
    floors: selectedFloors.value,
    staffRoles: selectedStaffRoles.value,
    nonStaffRoles: selectedRoles.value.map((r) => r.siteRoleId),
    tenants: selectedTenants.value.map((t) => t.tenantId),
    staffCollections: selectedStaffUserCollections.value,
    nonStaffCollections: selectedNonStaffCollections.value.map((c) => c.userCollectionId),
    allTenants: sendAllTenants.value,
    allStaff: sendAllStaff.value,
  };

  // If all floors selected, set to null (null is all floors)
  if (property.value.floorList.length == filter.floors.length) {
    filter.floors = null;
  }

  filteredRecipients.value = await alertStore.getFilteredRecipients(filter);
}

enum SortType {
  Name = 1,
  Role,
  Tenant,
  Floors,
  Push,
  Email,
  Phone,
}
let selectedSortType = ref(SortType.Name);

let sortedRecipients = computed(() => {
  console.log("Sort is " + selectedSortType.value.toString());
  return filteredRecipients.value?.sort((a, b) => {
    if (selectedSortType.value == SortType.Name)
      return a.firstName?.localeCompare(b.firstName);
    else if (selectedSortType.value === SortType.Role)
      return a.roleAlias?.localeCompare(b.roleAlias);
    else if (selectedSortType.value === SortType.Tenant)
      return a.tenantName?.localeCompare(b.tenantName);
    else if (selectedSortType.value === SortType.Floors)
      return a.alertSubscribedFloors?.localeCompare(b.alertSubscribedFloors);
    else if (selectedSortType.value === SortType.Push)
      return (a.pushSubscriptionCount ?? 0) - (b.pushSubscriptionCount ?? 0);
    else if (selectedSortType.value == SortType.Email)
      return a.contactEmail?.localeCompare(b.contactEmail);
    else if (selectedSortType.value === SortType.Phone)
      return a.contactSms?.localeCompare(b.contactSms);
  });
});

let isSending = ref(false);
let errorSending = ref(false);
let errorText = ref("");
let additionalErrorText = ref("");
let successSending = ref(false);

async function confirmSendAlert() {
  //Reset error sage
  errorText.value = "";
  errorSending.value = false;

  if (message.value.length == 0 || message.value.match(/^ *$/) !== null) {
    errorSending.value = true;
    errorText.value = "Message must not be empty.";
    successSending.value = false;
    return;
  }
  if (filteredCount.value + additionalRecipients.value.length == 0) {
    errorSending.value = true;
    errorText.value = "At least 1 recipient must be selected.";
    successSending.value = false;
    return;
  }

  isConfirmDialogVisible.value = true;
}

async function sendAlert() {
  // if (message.value.length == 0 || message.value.match(/^ *$/) !== null) {
  //   errorSending.value = true;
  //   errorText.value = "Message must not be empty.";
  //   successSending.value = false;
  //   return;
  // }
  // if (filteredCount.value + additionalRecipients.value.length == 0) {
  //   errorSending.value = true;
  //   errorText.value = "At least 1 recipient must be selected.";
  //   successSending.value = false;
  //   return;
  // }

  isSending.value = true;

  var filter: SendAlertRequest = {
    portalIds: [property.value.portalId],
    portalName: property.value.name,
    siteCode: property.value.siteCode,
    floors: selectedFloors.value,
    staffRoles: selectedStaffRoles.value,
    nonStaffRoles: selectedRoles.value.map((r) => r.siteRoleId),
    tenants: selectedTenants.value.map((t) => t.tenantId),
    staffCollections: selectedStaffUserCollections.value,
    nonStaffCollections: selectedNonStaffCollections.value.map((c) => c.userCollectionId),
    allTenants: sendAllTenants.value,
    allStaff: sendAllStaff.value,
    additionalPhoneNumbers: additionalRecipients.value,
    message: message.value,
  };

  // If all floors selected, set to null (null is all floors)
  if (property.value.floorList.length == filter.floors.length) {
    filter.floors = null;
  }

  let possibleErrorMessage = await alertStore.sendAlert(filter);

  if (!possibleErrorMessage) {
    errorSending.value = false;
    successSending.value = true;
  } else {
    errorSending.value = true;

    if (possibleErrorMessage.toLowerCase().indexOf("duplicate") >= 0) {
      errorText.value = "Message Error. Message cannot be identical to last sent message";
    } else {
      errorText.value = "Error sending alert.";
      additionalErrorText.value = possibleErrorMessage;
    }

    successSending.value = false;
  }

  isConfirmDialogVisible.value = false;
  isSending.value = false;
}
</script>
